import {
  Badge,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react";
import DataTable, { TableColumn } from "react-data-table-component";
import SimpleFormGenerator from "v2/components/shared/SimpleFormGenerator";
import { formatCNPJ } from "helpers/formats";
import React from "react";
import { createClientSearchPicker } from "v2/services/app/factories/forms/createClientSearchPicker";
import { createClientForGroupSearchPickerMulti } from "v2/services/app/factories/forms/createClientForGroupSearchPickerMulti";
import { axiosAuth } from "helpers/axiosInstances";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { UserModel } from "interfaces/user";
import { clearLoading, setGlobalMessage, showLoading } from "redux/actions";
import { isUnauthorized } from "helpers/errors";
import { BiTrash } from "react-icons/bi";

interface EditGroupProps {
  isOpenGroup: boolean;
  id: number;
  cbOnClose?: () => void;
}

const EditGroup: React.FC<EditGroupProps> = ({
  isOpenGroup,
  id,
  cbOnClose,
}) => {
  const [hashData, setHashData] = React.useState<any>({});
  const [data, setData] = React.useState<any[]>([]);
  const [shouldSync, setShouldSync] = React.useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const [userData, setUserData] = React.useState<UserModel>({});
  const getUser = async () => {
    try {
      dispatch(
        showLoading({
          message: "Carregando",
          isLoading: true,
        })
      );

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      };

      const { data } = await axiosAuth.get(`/users/${id}`, config);

      setUserData(data);
    } catch (error: any) {
      if (isUnauthorized(error)) {
        history.push("/unauthorized");
      } else {
        dispatch(
          setGlobalMessage({
            message: `Ocorreu um erro ao carregar os dados do usuário: ${error}`,
            type: "ERROR",
          })
        );
      }
    } finally {
      dispatch(clearLoading());
    }
  };

  async function add() {
    try {
      const customer = hashData?.customer;
      const group = hashData?.group;

      if (!customer) return;

      setData((old) => {
        const index = old.findIndex(
          (c) => c.id_syscambio === customer.id_syscambio
        );

        const mappedGroup = group
          ? group.map((g: any) => ({
              id_syscambio: g.id_syscambio,
              corporate_name: g.fantasy_name,
              document: g.document,
            }))
          : undefined;

        if (index !== -1) {
          return old.map((item, i) =>
            i === index
              ? {
                  ...item,
                  ...(mappedGroup && { group: mappedGroup }),
                }
              : item
          );
        }

        return [
          ...old,
          {
            id_syscambio: customer.id_syscambio,
            corporate_name: customer.fantasy_name,
            document: customer.document,
            group: mappedGroup ?? [],
          },
        ];
      });

      setHashData({});
      setShouldSync(true);
    } catch (error) {
      console.error(error);
    }
  }

  async function removeRoot(id_syscambio: number) {
    setData((old) =>
      old.filter((item) => Number(item.id_syscambio) !== Number(id_syscambio))
    );
    setShouldSync(true);
  }

  async function removeFromGroup(
    parentId_syscambio: number,
    groupId_syscambio: number
  ) {
    setData((old) =>
      old.map((item) => {
        if (Number(item.id_syscambio) === Number(parentId_syscambio)) {
          return {
            ...item,
            group: item.group.filter(
              (g: any) => Number(g.id_syscambio) !== Number(groupId_syscambio)
            ),
          };
        }
        return item;
      })
    );
    setShouldSync(true);
  }

  async function update() {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      };

      await axiosAuth.put(
        `/users/${id}`,
        {
          id: id,
          internal_group: data,
        },
        config
      );
    } catch (error) {}
  }

  const columnsB: TableColumn<any>[] = [
    {
      name: "id",
      selector: (row) => row.id_syscambio ?? "",
      sortable: true,
      omit: true,
    },
    {
      name: "Nome",
      selector: (row) => row.corporate_name ?? "",
      sortable: true,
    },
    {
      name: "Documento",
      selector: (row) => formatCNPJ(row.document ?? ""),
      sortable: true,
    },
    {
      name: "AÇÕES",
      cell: (item) => (
        <HStack m={1}>
          <IconButton
            aria-label="Remover empresa"
            size={"sm"}
            colorScheme={"red"}
            onClick={() => {
              removeRoot(item.id_syscambio);
            }}
          >
            <BiTrash size={16} />
          </IconButton>
        </HStack>
      ),
      ignoreRowClick: true,
      allowOverflow: false,
      width: "225px",
      button: true,
    },
  ];

  React.useEffect(() => {
    if (!isOpenGroup) return;
    getUser();
  }, [isOpenGroup]);

  React.useEffect(() => {
    setData(userData.internal_group || []);
  }, [userData]);

  React.useEffect(() => {
    if (!shouldSync) return;
    update();
    setShouldSync(false);
  }, [shouldSync]);

  const ExpandedComponent = ({ data }: { data: any }) => (
    <div>
      <h4>Empresas do grupo:</h4>
      <ul>
        {data.group && data.group.length > 0 ? (
          data.group.map((g: any) => (
            <li key={g.id_syscambio}>
              <Stack direction="row" spacing={4} align="center">
                <HStack m={1}>
                  <IconButton
                    aria-label="Remover empresa"
                    size={"sm"}
                    colorScheme={"red"}
                    onClick={() => {
                      removeFromGroup(data.id_syscambio, g.id_syscambio);
                    }}
                  >
                    <BiTrash size={16} />
                  </IconButton>
                </HStack>
                <Badge variant="outline" colorScheme="green">
                  {formatCNPJ(g.document)}
                </Badge>
                <Text fontWeight="bold">{g.corporate_name}</Text>
              </Stack>
            </li>
          ))
        ) : (
          <li>Nenhuma empresa no grupo.</li>
        )}
      </ul>
      <Divider />
    </div>
  );

  return (
    <>
      <Modal
        size={"6xl"}
        isOpen={isOpenGroup}
        onClose={() => {
          cbOnClose && cbOnClose();
          setHashData({});
          setData([]);
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edição das empresas do grupo</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <p>
              Selecione abaixo as empresas e grupo para adicionar a esse
              usuário:
            </p>
            <SimpleFormGenerator
              rows={[
                {
                  columns: 3,
                  fields: [
                    createClientSearchPicker({
                      label: `Empresa (Cadastrada) ${
                        hashData?.customer?.document
                          ? `(${formatCNPJ(hashData?.customer?.document)})`
                          : ""
                      }`,
                      name: "customer",
                      labelKey: "fantasy_name",
                    }),

                    hashData?.customer?.group_id
                      ? createClientForGroupSearchPickerMulti(
                          hashData?.customer?.group_id,
                          {
                            label: `Eps. Grupo ${
                              hashData?.group?.document
                                ? `(${formatCNPJ(hashData?.group?.document)})`
                                : ""
                            }`,
                            name: "group",
                            labelKey: "fantasy_name",
                          }
                        )
                      : undefined,
                  ],
                },
              ]}
              value={hashData}
              onChange={(v) => {
                const { fantasy_name, document, id_syscambio, group_id } =
                  v.customer || {};

                const nextParams: any = { ...v };

                if (v.customer) {
                  nextParams.customer = {
                    fantasy_name,
                    document,
                    id_syscambio,
                    group_id,
                  };
                }

                if (v.group) {
                  nextParams.group = v.group;
                }

                setHashData(nextParams);
              }}
            />
            <Flex>
              <Spacer />
              {hashData.customer && (
                <Button
                  variant={"outline"}
                  color={"blue.400"}
                  colorScheme={"blue"}
                  mt="10px"
                  mb="20px"
                  onClick={() => add()}
                >
                  Adicionar
                </Button>
              )}
            </Flex>

            <p>Abaixo remova as empresas do grupo:</p>

            <DataTable
              keyField="id_syscambio"
              noDataComponent=""
              columns={columnsB}
              data={data || []}
              dense={true}
              fixedHeader={true}
              fixedHeaderScrollHeight={"550px"}
              striped={true}
              expandableRows={true}
              expandableRowsComponent={ExpandedComponent}
            />
          </ModalBody>

          <ModalFooter>
            <Button
              variant={"ghost"}
              color={"red.400"}
              colorScheme={"red"}
              mr="20px"
              onClick={() => cbOnClose && cbOnClose()}
            >
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditGroup;
